import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { ApiError, api } from "../api/client";
import type { Invoice, Money } from "../api/types";
import { formatMoney } from "../lib/format";
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  balanceAfter,
  referenceRequired,
  todayInAccountZone,
  validatePayment,
  type PaymentValidationErrors,
} from "../lib/payment";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import { InvoiceStatusBadge } from "./InvoiceStatusBadge";
import { Button, Card, cx } from "./ui";

export { PAYMENT_METHODS };

/**
 * Record a manual receipt against an issued invoice (FR-25).
 *
 * One form, used in two places: the payments register (pick any payable
 * invoice) and an invoice's own page (the invoice is fixed). The screen is
 * deliberately separated into invoice context, payment details and a live
 * balance summary so labels, values and actions never share a line by accident.
 *
 * Client validation only keeps an obviously wrong submit off the wire. The
 * server re-checks the balance, invoice status and reference uniqueness inside
 * a transaction and remains the authority on all three.
 */

/** Invoices that may still receive money: issued, not paid in full and not void. */
const PAYABLE = new Set(["sent", "part_paid", "overdue"]);

const INPUT =
  "w-full min-w-0 rounded-md border-0 bg-white px-2.5 py-2 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900";
const INPUT_INVALID =
  "ring-rose-400 focus:ring-rose-500";

export function RecordPaymentForm({
  invoiceId,
  invoice: fixedInvoice,
  balanceDue,
  onDone,
  onCancel,
}: {
  /** When set, the invoice is fixed (recording from its detail page). */
  invoiceId?: string;
  /** The fixed invoice, so the summary can show its number, customer and total. */
  invoice?: Invoice | null;
  /** The fixed invoice's outstanding balance, for the hint and validation. */
  balanceDue?: Money | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const fixed = Boolean(invoiceId);
  const timeZone = useAccountTimeZone();

  const invoices = useQuery({
    queryKey: ["invoices", "payable"],
    queryFn: ({ signal }) => api.invoices({ page_size: 200 }, signal),
    select: (page) => ({
      ...page,
      results: page.results.filter((row) => PAYABLE.has(row.status)),
    }),
    staleTime: 30_000,
    enabled: !fixed,
  });

  const [selectedInvoiceId, setSelectedInvoiceId] = useState(invoiceId ?? "");
  const selected = invoices.data?.results.find((row) => row.id === selectedInvoiceId);
  const currentInvoice = fixed ? fixedInvoice ?? null : selected ?? null;

  const fixedBalance = Number(
    balanceDue?.amount ?? fixedInvoice?.balance_due?.amount ?? 0,
  );
  const outstanding = currentInvoice
    ? Number(currentInvoice.balance_due?.amount ?? 0)
    : fixedBalance;
  const currency =
    currentInvoice?.currency || balanceDue?.currency || fixedInvoice?.currency || "INR";

  const [amount, setAmount] = useState(
    fixed && fixedBalance > 0 ? fixedBalance.toFixed(2) : "",
  );
  const [method, setMethod] = useState<string>("cash");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [allowOverpayment, setAllowOverpayment] = useState(false);
  const [paymentDate, setPaymentDate] = useState(() => todayInAccountZone(timeZone));

  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const submitting = useRef(false);

  const errors = validatePayment({
    amount,
    paymentDate,
    method,
    reference,
    outstanding,
    allowOverpayment,
  });

  const valid = Boolean(selectedInvoiceId) && Object.keys(errors).length === 0;

  const showClientError = (key: keyof PaymentValidationErrors): string | undefined =>
    submitted || touched[key] ? errors[key] : undefined;

  const save = useMutation({
    mutationFn: () =>
      api.recordPayment({
        invoice: selectedInvoiceId,
        amount: amount.trim(),
        method,
        reference: reference.trim(),
        notes: notes.trim(),
        payment_date: paymentDate || undefined,
        allow_overpayment: allowOverpayment,
      }),
    onSuccess: onDone,
  });

  const serverFields = save.error instanceof ApiError ? save.error.fields : {};
  const serverError = (key: string): string | undefined =>
    serverFields[key]?.join(" ");
  const fieldError = (key: keyof PaymentValidationErrors): string | undefined =>
    showClientError(key) ?? serverError(key);

  const amountError = fieldError("amount");
  const dateError = fieldError("payment_date");
  const methodError = fieldError("method");
  const referenceError = fieldError("reference");
  const invoiceError = fixed ? undefined : serverError("invoice");

  const isVoid = currentInvoice?.status === "void" || fixedInvoice?.status === "void";

  const nonFieldError = serverError("non_field_errors");
  const detailError = serverError("detail");
  const invoiceServerError = serverError("invoice");
  const formError =
    (isVoid ? "Cannot record payments against void invoices." : undefined) ??
    nonFieldError ??
    detailError ??
    invoiceServerError ??
    (save.isError && Object.keys(serverFields).length === 0
      ? save.error instanceof ApiError
        ? save.error.message
        : "Could not record the payment."
      : undefined);

  function markTouched(key: keyof PaymentValidationErrors) {
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }

  function chooseInvoice(id: string) {
    setSelectedInvoiceId(id);
    const picked = invoices.data?.results.find((row) => row.id === id);
    const due = Number(picked?.balance_due?.amount ?? 0);
    setAmount(due > 0 ? due.toFixed(2) : "");
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (isVoid || !valid || submitting.current) return;
    // React Query state updates asynchronously, so a fast double-click can fire
    // twice before `isPending` paints. The ref closes that window; the server's
    // reference rule and balance lock close the rest.
    submitting.current = true;
    save.mutate(undefined, {
      onSettled: () => {
        submitting.current = false;
      },
    });
  }

  return (
    <Card className="mb-4 overflow-hidden" padded={false}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-slate-900">Record a payment</h2>
          <p className="mt-1 text-sm text-slate-500">
            Money received against an issued invoice. The invoice is updated on save.
          </p>
        </div>
        {currentInvoice ? (
          <InvoiceStatusBadge status={currentInvoice.display_status} />
        ) : null}
      </div>

      <form onSubmit={onSubmit}>
        <div className="grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          <div className="min-w-0 space-y-6">
            <section aria-labelledby="payment-invoice-heading" className="min-w-0">
              <SectionHeading
                id="payment-invoice-heading"
                title="Invoice"
                description={
                  fixed
                    ? "The invoice this payment will be applied to."
                    : "Only issued invoices with a balance are shown."
                }
              />

              {fixed ? (
                <InvoiceSummary invoice={fixedInvoice} balanceDue={balanceDue} />
              ) : (
                <div className="mt-3 space-y-3">
                  <Field id="payment-invoice" label="Invoice" error={invoiceError}>
                    <select
                      id="payment-invoice"
                      value={selectedInvoiceId}
                      onChange={(event) => chooseInvoice(event.target.value)}
                      aria-invalid={invoiceError ? true : undefined}
                      aria-describedby={
                        invoiceError ? "payment-invoice-error" : undefined
                      }
                      className={cx(INPUT, invoiceError && INPUT_INVALID)}
                    >
                      <option value="">Select an invoice…</option>
                      {invoices.data?.results.map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.number || "Draft"} · {row.contact_name || "—"} ·{" "}
                          {formatMoney(row.balance_due)} due
                        </option>
                      ))}
                    </select>
                  </Field>

                  {selected ? <InvoiceSummary invoice={selected} /> : null}
                </div>
              )}
            </section>

            <section aria-labelledby="payment-details-heading" className="min-w-0">
              <SectionHeading
                id="payment-details-heading"
                title="Payment details"
                description="Amount, date and the method the money arrived by."
              />

              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field
                  id="payment-amount"
                  label="Amount received"
                  error={amountError}
                >
                  <input
                    id="payment-amount"
                    inputMode="decimal"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    onBlur={() => markTouched("amount")}
                    placeholder="0.00"
                    aria-invalid={amountError ? true : undefined}
                    aria-describedby={amountError ? "payment-amount-error" : undefined}
                    className={cx(INPUT, amountError && INPUT_INVALID)}
                  />
                </Field>

                <Field
                  id="payment-date"
                  label="Payment date"
                  error={dateError}
                >
                  <input
                    id="payment-date"
                    type="date"
                    value={paymentDate}
                    onChange={(event) => setPaymentDate(event.target.value)}
                    onBlur={() => markTouched("payment_date")}
                    aria-invalid={dateError ? true : undefined}
                    aria-describedby={dateError ? "payment-date-error" : undefined}
                    className={cx(INPUT, dateError && INPUT_INVALID)}
                  />
                </Field>

                <Field id="payment-method" label="Method" error={methodError}>
                  <select
                    id="payment-method"
                    value={method}
                    onChange={(event) => setMethod(event.target.value)}
                    onBlur={() => markTouched("method")}
                    aria-invalid={methodError ? true : undefined}
                    aria-describedby={methodError ? "payment-method-error" : undefined}
                    className={cx(INPUT, methodError && INPUT_INVALID)}
                  >
                    {PAYMENT_METHODS.map((value) => (
                      <option key={value} value={value}>
                        {PAYMENT_METHOD_LABELS[value]}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field
                  id="payment-reference"
                  label="Reference"
                  error={referenceError}
                  hint={
                    referenceRequired(method)
                      ? "Required for this method: UPI, cheque or transaction id."
                      : "Optional for cash."
                  }
                >
                  <input
                    id="payment-reference"
                    value={reference}
                    onChange={(event) => setReference(event.target.value)}
                    onBlur={() => markTouched("reference")}
                    placeholder={
                      method === "cheque"
                        ? "Cheque number"
                        : method === "cash"
                          ? "Optional"
                          : "UPI / card / transaction id"
                    }
                    aria-invalid={referenceError ? true : undefined}
                    aria-describedby={
                      referenceError
                        ? "payment-reference-error"
                        : "payment-reference-hint"
                    }
                    className={cx(INPUT, referenceError && INPUT_INVALID)}
                  />
                </Field>

                <Field id="payment-notes" label="Notes" className="sm:col-span-2">
                  <textarea
                    id="payment-notes"
                    rows={2}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder="Anything worth remembering about this payment."
                    className={INPUT}
                  />
                </Field>

                <label className="flex items-start gap-2 text-sm text-slate-700 sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={allowOverpayment}
                    onChange={(event) => setAllowOverpayment(event.target.checked)}
                    className="mt-0.5 size-4 rounded border-slate-300"
                  />
                  <span>
                    Allow overpayment
                    <span className="mt-0.5 block text-xs text-slate-500">
                      Record the excess as an advance on this customer. Only use this when the
                      extra money is genuinely a deposit or credit.
                    </span>
                  </span>
                </label>
              </div>
            </section>

            {formError ? (
              <p
                role="alert"
                className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700"
              >
                {formError}
              </p>
            ) : null}
          </div>

          <aside className="min-w-0 lg:border-l lg:border-slate-200 lg:pl-6">
            <PaymentSummary
              visible={Boolean(currentInvoice) || Boolean(balanceDue)}
              outstanding={outstanding}
              amount={amount}
              currency={currency}
            />
          </aside>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-end">
          <Button onClick={onCancel} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={save.isPending || !valid || isVoid}
          >
            {save.isPending ? "Recording…" : "Record payment"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function SectionHeading({
  id,
  title,
  description,
}: {
  id: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="min-w-0">
      <h3
        id={id}
        className="text-xs font-semibold uppercase tracking-wide text-slate-500"
      >
        {title}
      </h3>
      {description ? (
        <p className="mt-1 text-xs text-slate-500">{description}</p>
      ) : null}
    </div>
  );
}

function InvoiceSummary({
  invoice,
  balanceDue,
}: {
  invoice?: Invoice | null;
  balanceDue?: Money | null;
}) {
  if (!invoice) {
    return (
      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Outstanding balance
        </p>
        <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900">
          {formatMoney(balanceDue)}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold tabular-nums text-slate-900">
            {invoice.number || "Draft"}
          </p>
          <p className="mt-0.5 truncate text-sm text-slate-600">
            {invoice.contact_name || "—"}
          </p>
          {invoice.contact_phone ? (
            <p className="truncate text-xs text-slate-500">{invoice.contact_phone}</p>
          ) : null}
        </div>
        <InvoiceStatusBadge status={invoice.display_status} />
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-slate-200 pt-3 text-sm">
        <SummaryValue label="Invoice total" value={formatMoney(invoice.total_amount)} />
        <SummaryValue label="Already paid" value={formatMoney(invoice.amount_paid)} />
      </dl>
    </div>
  );
}

function PaymentSummary({
  visible,
  outstanding,
  amount,
  currency,
}: {
  visible: boolean;
  outstanding: number;
  amount: string;
  currency: string;
}) {
  const value = Number(amount);
  const hasAmount =
    amount.trim() !== "" && Number.isFinite(value) && value > 0;
  const remaining = balanceAfter(outstanding, amount);
  const after = hasAmount && remaining !== null ? remaining : outstanding;

  const money = (value: number): Money => ({
    amount: value.toFixed(2),
    currency,
  });

  return (
    <section
      aria-labelledby="payment-summary-heading"
      className="rounded-lg border border-slate-200 bg-slate-50 p-4"
    >
      <h3
        id="payment-summary-heading"
        className="text-xs font-semibold uppercase tracking-wide text-slate-500"
      >
        Payment summary
      </h3>

      {!visible ? (
        <p className="mt-2 text-sm text-slate-500">
          Select an invoice to see its balance and what will remain after this
          payment.
        </p>
      ) : (
        <dl className="mt-3 space-y-2.5 text-sm">
          <SummaryValue
            label="Outstanding balance"
            value={formatMoney(money(outstanding))}
          />
          <SummaryValue
            label="This payment"
            value={hasAmount ? formatMoney(money(value)) : "—"}
          />
          <div className="border-t border-slate-200 pt-2.5">
            <SummaryValue
              label="Balance after payment"
              value={formatMoney(money(after))}
              prominent
            />
          </div>
        </dl>
      )}
    </section>
  );
}

function SummaryValue({
  label,
  value,
  prominent,
}: {
  label: string;
  value: string;
  prominent?: boolean;
}) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-4">
      <dt className={cx("min-w-0 text-slate-500", prominent && "font-medium text-slate-700")}>
        {label}
      </dt>
      <dd
        className={cx(
          "shrink-0 text-right tabular-nums",
          prominent ? "text-base font-semibold text-slate-900" : "text-slate-900",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function Field({
  id,
  label,
  children,
  error,
  hint,
  className,
}: {
  id: string;
  label: string;
  children: ReactNode;
  error?: string | undefined;
  hint?: string | undefined;
  className?: string;
}) {
  return (
    <div className={cx("min-w-0", className)}>
      <label
        htmlFor={id}
        className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500"
      >
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs text-rose-600">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1.5 text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
